import ExpandMoreOutlined from "@mui/icons-material/ExpandMoreOutlined";
import classNames from "classnames";
import type { ICollapsableTable } from "components/shared/CollapsableTable";
import Table from "components/shared/CollapsableTable";
import type { TableSortOptions } from "components/shared/CollapsableTable/types";
import { H3, SmallBody } from "helpers/TypographyComponents";
import { useEffect, useRef, useState } from "react";

function Tables({
    label,
    tables,
    isPending,
    onReflow,
    isCollapsed,
    setIsCollapsed,
    sort,
    onColumnClick,
}: {
    label: string;
    tables: ICollapsableTable[];
    isPending?: boolean;
    onReflow?: () => void;
    isCollapsed: boolean;
    setIsCollapsed: (x: boolean) => void;
    sort?: TableSortOptions;
    onColumnClick?: (args: { id: string }) => void;
}) {
    const [hasInteracted, setHasInteracted] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const getTableWidth = (table: ICollapsableTable, tableIndex: number) => {
        return tableIndex === 0
            ? undefined
            : table.columns.reduce<number>(
                  (sum, t) => (t.width ? sum + t.width : sum),
                  2 * 12, // row horizontal padding (c.f. Table/Row/Template)
              );
    };

    useEffect(() => {
        const elem = ref.current;

        const handleTransitionEnd = () => {
            onReflow?.();
        };

        if (elem) {
            elem.addEventListener("transitionend", handleTransitionEnd);

            return () => {
                elem?.removeEventListener?.("transitionend", handleTransitionEnd);
            };
        }
    }, [onReflow]);

    return (
        <div className="relative flex flex-col gap-1">
            <div
                className="sticky top-0 flex items-end gap-4 bg-white! cursor-pointer! z-10 px-0! py-2! border-[#eaeaea]!"
                onClick={() => {
                    setHasInteracted(true);
                    setIsCollapsed(!isCollapsed);
                }}
            >
                {tables.map((table, i) => {
                    return (
                        <div
                            key={table.id}
                            className={classNames(
                                "relative flex justify-end h-[1lh]!",
                                i === 0 ? "flex-1 min-w-0" : "flex-none",
                            )}
                            style={{
                                width: getTableWidth(table, i),
                            }}
                        >
                            {i === 0 ? (
                                <div className="relative flex-1 min-w-0">
                                    <div
                                        ref={ref}
                                        className="absolute left-0 bottom-0 h-full flex items-center -translate-x-full"
                                    >
                                        <ExpandMoreOutlined
                                            className={classNames([
                                                "transition-transform!",
                                                {
                                                    "-rotate-90": isCollapsed,
                                                },
                                            ])}
                                        />
                                    </div>
                                    <H3>{label}</H3>
                                </div>
                            ) : null}
                            <SmallBody className="flex-none whitespace-nowrap text-right!">
                                {table.title || ""}
                            </SmallBody>
                        </div>
                    );
                })}
            </div>

            <div>
                <div className="flex items-start gap-4 px-[0px]! pb-[1rem]!">
                    {tables.map((table, i) => {
                        return (
                            <div
                                key={table.id}
                                className={classNames("relative", i === 0 ? "flex-1 min-w-0" : "flex-none")}
                                style={{
                                    width: getTableWidth(table, i),
                                }}
                            >
                                <Table
                                    {...table}
                                    isCollapsed={isCollapsed}
                                    isPending={isPending}
                                    isAnimated={hasInteracted}
                                    sort={sort}
                                    onColumnClick={onColumnClick}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default Tables;
